import {ID_PREFIX, IPC_VERSION} from "../utils/constants";
import {debug, error, warn} from "../utils/log";
import {later} from "../utils/basic";
import {createContext} from "./context-shared";
import {leader} from "./ipc-leader";
import {
    type IpcMessage,
    type IpcMessageRequestDump,
    isIpcMessage,
    isResendableMessage,
    type ResendableIpcMessage
} from "./ipc-shared";


const context = createContext({
    state: 'initializing',
    solution: '',
    rate: 0,
    errorMsg: '',
    errorId: ''
})

export const data = context.view
export const onChanges = context.onChanges

onChanges('*', () => {
    // if we are not also the leader, we copy the shared data to the leader data so that when we become the leader, we have the latest state
    if (!isLeader) {
        leaderData = structuredClone(data);
    }
})

let isLeader = false;


const inFlight = new Map<string, { message: IpcMessage; resolve: () => void, timer: number }>();

function sendWithRetry(message: ResendableIpcMessage, retries = 5, delay = 100) {
    if (retries <= 0) {
        return;
    }
    followerChannel.postMessage(message);
    setTimeout(() => {
        if (inFlight.has(message.id)) {
            debug(`Retry for message ${message.id}, remaining attempts: ${retries - 1}`);
            sendWithRetry(message, retries - 1, delay * 2); // Exponential Backoff
        }
    }, delay);
}

function sendToLeader(message: ResendableIpcMessage, timeout = 10000) {
    return new Promise<void>((resolve, reject) => {
        if (!isResendableMessage(message)) {
            throw new Error("Message must have an id");
        }
        const id = message.id;
        const timer = window.setTimeout(() => {
            inFlight.delete(id);
            reject(new Error(`Message ${id} timed out after ${timeout}ms`));
        }, timeout);
        inFlight.set(id, {message, resolve, timer});
        sendWithRetry(message);
    });
}

let gotStateDump = false;
const openLeaderRequests: ResendableIpcMessage[] = []
const followerChannel = new BroadcastChannel(`${ID_PREFIX}ipc`);
followerChannel.onmessage = async (event) => {
    try {
        if (!isIpcMessage(event.data)) {
            warn("Received non-IPC message:", event.data);
            return;
        }
        const data = event.data;
        switch (data.type) {
            case 'request-validation':
            case 'request-dump':
                // get handled by the leader, but we keep track when we become the leader
                if (isResendableMessage(data) && !isLeader) {
                    openLeaderRequests.push(data);
                }
                break;
            case 'version':
                if (data.version > IPC_VERSION) {
                    warn("It looks like there is a newer version of the IPC protocol. I need to reload to load the new version.");
                    location.reload();
                }
                break;
            case 'state-dump':
                context.updateState(data.state);
                gotStateDump = true;
                break;
            case "checksum":
                const checksum = await context.getChecksum();
                if (data.checksum.length !== checksum.length || !data.checksum.every((value, index) => value === checksum[index])) {
                    warn("Checksum mismatch, state might be out of sync.");
                    sendToLeader({
                        type: 'request-dump',
                        id: crypto.randomUUID()
                    } as IpcMessageRequestDump).catch(err => {
                        error("Failed to request state dump:", err);
                    });
                }
                break;
            case 'ack':
                const leaderRequestIndex = openLeaderRequests.findIndex(req => req.id === data.id);
                if (leaderRequestIndex !== -1) {
                    openLeaderRequests.splice(leaderRequestIndex, 1);
                }
                if (inFlight.has(data.id)) {
                    const {resolve, timer} = inFlight.get(data.id)!;
                    inFlight.delete(data.id);
                    clearTimeout(timer);
                    resolve();
                    debug(`Acknowledged message ${data.id}`);
                }
                break;
            default:
                warn("Unknown IPC message:", data);
                break;
        }
    } catch (err) {
        error("Failed to handle IPC message:", event, err);
    }
};

// Announce our version to other tabs. When they are older, they will reload.
postMessage({type: 'version', version: IPC_VERSION})
setInterval(() => postMessage({type: 'version', version: IPC_VERSION}), 120 * 1000); // every two minutes

/** Promise that resolves when the initial state is set, or we get an error while trying to reach the leader */
export const waitForInitialState = (async () => {
    debug("Requesting initial state from leader.");
    // Request state from other tabs
    await sendToLeader({
        type: 'request-dump',
        id: crypto.randomUUID()
    } as IpcMessageRequestDump);
    // busy-wait until we got the state dump
    while (!gotStateDump) {
        await later(1)
    }
    debug("Received initial state from leader.", data);
})()

/**
 * Sends a validation request to the leader node. The leader decides whether to start the validation process based on the current state. When the validation fails, the leader will get a new solution.
 *
 * @return {Promise<void>} A promise that resolves when the request is successfully sent. The leader will handle the validation process asynchronously. You can listen to the state changes to get the result.
 */
export async function requestValidation(): Promise<void> {
    debug("Requesting validation from leader.");
    await sendToLeader({
        type: 'request-validation',
        id: crypto.randomUUID()
    })
}

let leaderData = context.getSnapshot();
leader(leaderData, openLeaderRequests, (newLeaderState) => {
    isLeader = newLeaderState;
    debug("Leader state changed:", newLeaderState);
})
